import {child, DatabaseReference, equalTo, get, getDatabase, orderByChild, orderByKey, push, query, ref, set} from "firebase/database";
import type {IndustryUnits} from "./index.js";
import {Business, Expense, firebaseApp, Industry, Milestone, Pricing, Product} from "./index.js";


const rootDbRef = ref(getDatabase(firebaseApp));

const INDUSTRIES_NODE = "INDUSTRIES";
const industriesNodeRef: DatabaseReference = child(rootDbRef, INDUSTRIES_NODE);

const CURRENT_INDUSTRY_NODE = "CURRENT_INDUSTRY";
const currentIndustryNodeRef: DatabaseReference = child(rootDbRef, CURRENT_INDUSTRY_NODE);

const PRODUCTS_NODE = "PRODUCTS";
const productsNodeRef: DatabaseReference = child(rootDbRef, PRODUCTS_NODE);

const BUSINESSES_NODE = "BUSINESSES";
const businessesNodeRef: DatabaseReference = child(rootDbRef, BUSINESSES_NODE);

const PRICINGS_NODE = "PRICINGS";
const pricingsNodeRef: DatabaseReference = child(rootDbRef, PRICINGS_NODE);

const MILESTONES_NODE = "MILESTONES";
const milestonesNodeRef: DatabaseReference = child(rootDbRef, MILESTONES_NODE);

const EXPENSES_NODE = "EXPENSES";
const expensesNodeRef: DatabaseReference = child(rootDbRef, EXPENSES_NODE);


//********************************************************************************
//* GENERATE KEY
//********************************************************************************


export const generateMainFirebaseRtdbKey = (): string => {
	  return push(milestonesNodeRef).key;
};

export const generateStringKeyFromMillis = (): string => {
	  return Date.now().toString();
};


//********************************************************************************
//* INDUSTRIES
//********************************************************************************


export const createIndustriesHookQuery = (uid: string) => {
	  return query(child(industriesNodeRef, uid), orderByKey());
};

export const createIndustryHookQuery = (uid: string, industryKey: string) => {
	  return query(child(industriesNodeRef, uid), orderByKey(), equalTo(industryKey));
};

export const createCurrentIndustryHookQuery = (uid: string) => {
	  return child(currentIndustryNodeRef, uid);
};

export const addIndustry = (uid: string, industryKey: string, industry: Industry, onAdded: ()=>void) => {
	  const pathRef = child(industriesNodeRef, `${uid}/${industryKey}`);
	  set(pathRef, industry)
				 .then(
							() => {
								  onAdded();
							},
							(error) => {
								  console.error("Error adding Industry: ", error);
							}
				 );
};

export const updateIndustryUnits = (uid: string, industryKey: string, industryUnits: IndustryUnits, onUnitAdded: ()=>void) => {
	  const pathRef = child(industriesNodeRef, `${uid}/${industryKey}/industryUnits`);
	  set(pathRef, industryUnits)
				 .then(
							() => {
								  onUnitAdded();
							},
							(error) => {
								  console.error("Error updating Industry Units: ", error);
							}
				 );
};

export const getIndustry = (uid: string, industryKey: string, onGot: (industry: Industry) => void) => {
	  const pathRef = child(industriesNodeRef, `${uid}/${industryKey}`);
	  get(pathRef)
				 .then(
							(snapshot) => {
								  snapshot.exists() ? onGot(Industry.fromObject(snapshot.val())) : onGot(null);
							},
							(error) => {
								  console.error("Error getting Industry: ", error);
							}
				 );
};

export const setCurrentIndustryKey = (uid: string, industryKey: string, onSet: () => void) => {
	  const pathRef = child(currentIndustryNodeRef, uid);
	  set(pathRef, industryKey)
				 .then(
							() => {
								  onSet();
							},
							(error) => {
								  console.error("Error setting current Industry: ", error);
							}
				 );
};


//********************************************************************************
//* PRODUCTS
//********************************************************************************


export const createProductsHookQuery = (uid: string, industryKey: string) => {
	  return query(child(productsNodeRef, `${uid}/${industryKey}`), orderByKey());
};

export const addProduct = (uid: string, industryKey: string, productKey: string, product: Product) => {
	  const pathRef = child(productsNodeRef, `${uid}/${industryKey}/${productKey}`);
	  set(pathRef, product)
				 .then(
							() => {
							},
							(error) => {
								  console.error("Error adding Product: ", error);
							}
				 );
};


//********************************************************************************
//* BUSINESS
//********************************************************************************


export const createBusinessesHookQuery = (uid: string, industryKey: string) => {
	  return query(child(businessesNodeRef, `${uid}/${industryKey}`), orderByKey());
};

export const addBusiness = (uid: string, industryKey: string, businessKey: string, business: Business) => {
	  const pathRef = child(businessesNodeRef, `${uid}/${industryKey}/${businessKey}`);
	  set(pathRef, business)
				 .then(
							() => {
							},
							(error) => {
								  console.error("Error adding Business: ", error);
							}
				 );
};


//********************************************************************************
//* PRICINGS
//********************************************************************************


export const createPricingsHookQuery = (uid: string, industryKey: string) => {
	  return query(child(pricingsNodeRef, `${uid}/${industryKey}`), orderByKey());
};

export const addPricing = (uid: string, industryKey: string, pricingKey: string, pricing: Pricing) => {
	  const pathRef = child(pricingsNodeRef, `${uid}/${industryKey}/${pricingKey}`);
	  set(pathRef, pricing)
				 .then(
							() => {
							},
							(error) => {
								  console.error("Error adding Pricing: ", error);
							}
				 );
};

export const updatePrice = (uid: string, industryKey: string, pricingKey: string, price: number) => {
	  const pathRef = child(pricingsNodeRef, `${uid}/${industryKey}/${pricingKey}/price`);
	  set(pathRef, price)
				 .then(
							() => {
							},
							(error) => {
								  console.error("Error updating Price: ", error);
							}
				 );
};


//********************************************************************************
//* MILESTONES
//********************************************************************************


export const createMilestonesHookQuery = (uid: string, industryKey: string) => {
	  return query(child(milestonesNodeRef, `${uid}/${industryKey}`), orderByChild(Milestone.millisPath()));
};

export const addMilestone = (uid: string, industryKey: string, milestoneKey: string, milestone: Milestone) => {
	  const pathRef = child(milestonesNodeRef, `${uid}/${industryKey}/${milestoneKey}`);
	  set(pathRef, milestone)
				 .then(
							() => {
							},
							(error) => {
								  console.error("Error adding Milestone: ", error);
							}
				 );
};

export const markMilestoneAsAchieved = (uid: string, industryKey: string, milestoneKey: string, achievedOn: number) => {
	  let pathRef = child(milestonesNodeRef, `${uid}/${industryKey}/${milestoneKey}/${Milestone.isAchievedPath()}`);
	  set(pathRef, true)
				 .then(
							() => {
								  pathRef = child(milestonesNodeRef, `${uid}/${industryKey}/${milestoneKey}/${Milestone.achievedOnPath()}`);
								  set(pathRef, achievedOn)
											 .then(
														() => {
														},
														(error) => {
															  console.error("Error marking Milestone as achieved: ", error);
														}
											 );
							},
							(error) => {
								  console.error("Error marking Milestone as achieved: ", error);
							}
				 );
};


//********************************************************************************
//* EXPENSES
//********************************************************************************


export const createExpensesHookQuery = (uid: string, industryKey: string) => {
	  return query(child(expensesNodeRef, `${uid}/${industryKey}`), orderByChild(Expense.millisPath()));
};

export const addExpense = (uid: string, industryKey: string, expenseKey: string, expense: Expense) => {
	  const pathRef = child(expensesNodeRef, `${uid}/${industryKey}/${expenseKey}`);
	  set(pathRef, expense)
				 .then(
							() => {
							},
							(error) => {
								  console.error("Error adding Expense: ", error);
							}
				 );
};


export const nodeRefs: DatabaseReference[] = [
	  currentIndustryNodeRef,
	  industriesNodeRef,
	  productsNodeRef,
	  businessesNodeRef,
	  pricingsNodeRef,
	  milestonesNodeRef,
	  expensesNodeRef
];
